import React, { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { BookOpen, Search } from "lucide-react";
import { GLOSSARY } from "@/data/glossary";

// Glossary sheet + trigger button. Two variants:
//   * `floating` — fixed to viewport top-right so it follows the user as they scroll
//   * `inline` — normal button you drop wherever
export default function GlossarySidebar({ variant = "floating" }) {
  const [q, setQ] = useState("");

  const triggerCls = variant === "floating"
    ? "fixed z-40 top-24 right-6 md:right-8 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:bg-primary/90 transition-all px-4 py-2.5 flex items-center gap-2 text-xs mono uppercase tracking-widest"
    : "inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-3.5 py-2 text-xs mono uppercase tracking-widest transition-colors";

  const groups = useMemo(() => {
    const query = q.trim().toLowerCase();
    const filtered = query
      ? GLOSSARY.filter(
          (g) =>
            g.term.toLowerCase().includes(query) ||
            (g.aliases || []).some((a) => a.toLowerCase().includes(query)) ||
            g.definition.toLowerCase().includes(query)
        )
      : GLOSSARY;
    const byLetter = {};
    for (const g of filtered) {
      const L = g.letter || g.term.charAt(0).toUpperCase();
      (byLetter[L] = byLetter[L] || []).push(g);
    }
    return Object.entries(byLetter).sort(([a], [b]) => a.localeCompare(b));
  }, [q]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className={triggerCls}
          data-testid="glossary-open"
          aria-label="Open glossary"
        >
          <BookOpen className="w-4 h-4" />
          Glossary
          <span className="ml-1 mono text-[10px] opacity-80 tabular-nums">{GLOSSARY.length}</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[400px] sm:w-[440px] flex flex-col p-0">
        <SheetHeader className="p-6 pb-3 border-b border-border">
          <SheetTitle className="serif text-2xl">Investor glossary</SheetTitle>
          <SheetDescription>
            Every dividend term you&apos;ll bump into on DPP. Tap any underlined word in the article to jump straight to its definition.
          </SheetDescription>
          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 25 terms…"
              className="pl-9"
              data-testid="glossary-search"
              autoFocus
            />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="glossary-list">
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              No terms match &ldquo;{q}&rdquo;.
            </p>
          ) : (
            groups.map(([letter, items]) => (
              <div key={letter} className="mb-5">
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary sticky top-0 bg-background py-1">
                  {letter}
                </div>
                <ul className="space-y-3 mt-1.5">
                  {items.map((g) => (
                    <li key={g.term} data-testid={`glossary-item-${g.term.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="serif text-base">{g.term}</div>
                      {(g.aliases || []).length > 0 && (
                        <div className="mono text-[10px] text-muted-foreground uppercase tracking-widest">
                          also: {(g.aliases || []).join(", ")}
                        </div>
                      )}
                      <p className="text-sm text-foreground/85 mt-1 leading-relaxed">{g.definition}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
